import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { Chip } from '../design-system/Chip';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { useSessionStore } from '../../stores/sessionStore';
import { qualificationLabels, variantLabels } from '../../tokens';
import { fastagProfiles } from '../../fixtures/fastagProfiles';
import { Moon, Sun } from 'lucide-react';
import type { VariantKey, QualificationState, ListLength } from '../../types';

const variants: VariantKey[] = ['recommended', 'A', 'B', 'C', 'D', 'E', 'F'];
const qualifications: QualificationState[] = ['unqualified', 'low', 'negative', 'predicted-low'];
const listLengths: { value: ListLength; label: string }[] = [
  { value: 'empty', label: 'Empty' },
  { value: 'short', label: 'Short (1)' },
  { value: 'long', label: 'Long (6)' },
  { value: 'mixed', label: 'Mixed paid/pending' },
];
const balancePresets = [0, 47, -32, 200, 500];

export function ReviewModePanel() {
  const store = usePrototypeStore();
  const session = useSessionStore();

  const currentBalance = store.balanceValue ?? fastagProfiles[store.qualificationState]?.balance ?? 0;

  return (
    <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-elevation-2 dark:border-slate-700 dark:bg-slate-800">
      <Text variant="title">Review controls</Text>
      <Text variant="caption" color="secondary" className="mt-1">
        Switch variants, qualification, and states instantly.
      </Text>

      {/* Variant */}
      <Section title="Variant">
        <div className="grid grid-cols-2 gap-2">
          {variants.map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`variant-${v}`}
              onClick={() => store.setActiveVariant(v)}
              className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                store.activeVariant === v
                  ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              {variantLabels[v]}
              {v === 'recommended' && <Chip variant="primary" size="sm" className="ml-2">default</Chip>}
              {v === 'E' && <Chip variant="warning" size="sm" className="ml-2">rejected</Chip>}
            </button>
          ))}
        </div>
      </Section>

      {/* Qualification */}
      <Section title="Qualification">
        <div className="grid grid-cols-2 gap-2">
          {qualifications.map((q) => (
            <button
              key={q}
              type="button"
              data-testid={`qualification-${q}`}
              onClick={() => store.setQualificationState(q)}
              className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                store.qualificationState === q
                  ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              {qualificationLabels[q]}
            </button>
          ))}
        </div>
      </Section>

      {/* Balance override */}
      <Section title="Balance override">
        <div className="flex flex-wrap gap-2">
          {balancePresets.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => store.setBalanceValue(b)}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                store.balanceValue === b
                  ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              ₹{b}
            </button>
          ))}
          <button
            type="button"
            onClick={() => store.setBalanceValue(null)}
            className={`rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
              store.balanceValue === null
                ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
            }`}
          >
            Auto
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-600">
          <span className="text-sm text-slate-500">₹</span>
          <input
            type="number"
            value={currentBalance}
            onChange={(e) => store.setBalanceValue(Number(e.target.value))}
            className="w-full bg-transparent text-sm outline-none"
            aria-label="Custom balance"
          />
        </div>
      </Section>

      {/* List length */}
      <Section title="Challan list">
        <div className="grid grid-cols-2 gap-2">
          {listLengths.map((l) => (
            <button
              key={l.value}
              type="button"
              onClick={() => store.setListLength(l.value)}
              className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                store.listLength === l.value
                  ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Device + Theme */}
      <Section title="Device & theme">
        <div className="flex gap-2">
          <DeviceButton active={store.deviceFrame === 'android'} onClick={() => store.setDeviceFrame('android')}>
            Android
          </DeviceButton>
          <DeviceButton active={store.deviceFrame === 'iphone'} onClick={() => store.setDeviceFrame('iphone')}>
            iPhone
          </DeviceButton>
          <DeviceButton active={store.theme === 'light'} onClick={() => store.setTheme('light')}>
            <Sun className="h-4 w-4" /> Light
          </DeviceButton>
          <DeviceButton active={store.theme === 'dark'} onClick={() => store.setTheme('dark')}>
            <Moon className="h-4 w-4" /> Dark
          </DeviceButton>
        </div>
      </Section>

      {/* Simulation toggles */}
      <Section title="Simulate states">
        <div className="flex flex-wrap gap-2">
          <Toggle active={store.simulateLoading} onClick={() => store.setSimulateLoading(!store.simulateLoading)}>
            Loading
          </Toggle>
          <Toggle active={store.simulateEmpty} onClick={() => store.setSimulateEmpty(!store.simulateEmpty)}>
            Empty
          </Toggle>
          <Toggle active={store.simulateOffline} onClick={() => store.setSimulateOffline(!store.simulateOffline)}>
            Offline
          </Toggle>
          <Toggle active={store.simulateError} onClick={() => store.setSimulateError(!store.simulateError)}>
            Error
          </Toggle>
          <Toggle active={session.rechargeStatus === 'completed'} onClick={() => session.resetRecharge()}>
            Reset recharge
          </Toggle>
          <Toggle active={session.dismissedNudgeIds.length > 0} onClick={() => session.resetDismissals()}>
            Reset dismissals
          </Toggle>
        </div>
      </Section>

      <div className="mt-4 flex gap-2">
        <Button size="sm" variant="outline" fullWidth onClick={() => session.resetAll()}>
          Reset journey
        </Button>
        <Button size="sm" variant="ghost" fullWidth onClick={() => store.reset()}>
          Reset controls
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5 border-t border-slate-100 pt-4 first:mt-3 first:border-t-0 first:pt-0 dark:border-slate-700">
      <Text variant="label" color="secondary" className="mb-2 uppercase tracking-wide">
        {title}
      </Text>
      {children}
    </div>
  );
}

function DeviceButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
        active
          ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
        active
          ? 'bg-park-600 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
      }`}
    >
      {children}
    </button>
  );
}
